import { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { categoryService, newsService, applicationService, pageContentService } from '@/services/storage';
import type { ProductCategory, News, Application, PageContent } from '@/types';

export default function Home() {
  const [categories, setCategories] = useState<ProductCategory[]>([]);
  const [news, setNews] = useState<News[]>([]);
  const [applications, setApplications] = useState<Application[]>([]);
  const [pageContent, setPageContent] = useState<PageContent | null>(null);
  const productSliderRef = useRef<HTMLDivElement>(null);
  const appSliderRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const loadData = async () => {
      const [cats, newsList, apps, content] = await Promise.all([
        categoryService.getAll(),
        newsService.getAll(),
        applicationService.getAll(),
        pageContentService.get()
      ]);
      setCategories(cats);
      setNews(newsList.slice(0, 3));
      setApplications(apps);
      setPageContent(content);
    };
    loadData();
  }, []);

  if (!pageContent) {
    return <div className="min-h-screen flex items-center justify-center">加载中...</div>;
  }

  const scrollProducts = (direction: 'left' | 'right') => {
    if (productSliderRef.current) {
      const scrollAmount = 320;
      productSliderRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth',
      });
    }
  };

  const scrollApps = (direction: 'left' | 'right') => {
    if (appSliderRef.current) {
      const scrollAmount = 320;
      appSliderRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth',
      });
    }
  };

  const hero = pageContent.hero || {};
  const about = pageContent.about || {};
  const philosophy = pageContent.philosophy || { items: [] };
  const productsIntro = pageContent.productsIntro || {};

  return (
    <div>
      {/* Hero Section */}
      <section className="relative h-screen min-h-[600px] overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0 bg-blue-900">
          <div className="absolute inset-0 bg-gradient-to-r from-blue-900/90 to-blue-800/70 z-10" />
          <img
            src={hero.backgroundImage || '/hero-bg.jpg'}
            alt="Hero"
            className="w-full h-full object-cover"
          />
        </div>

        {/* Hero Content */}
        <div className="relative z-20 h-full flex items-center">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl">
              <h1 
                className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight whitespace-pre-line"
                style={{ 
                  fontSize: `${hero.titleFontSize || 48}px`,
                  lineHeight: hero.titleLineHeight || 1.4,
                  color: hero.titleColor || '#1e3a8a'
                }}
              >
                {hero.title || '专注于高性能、高品质\n数模混合芯片研发的\n芯片设计公司'}
              </h1>
              <p 
                className="text-xl mb-8"
                style={{ 
                  fontSize: `${hero.subtitleFontSize || 20}px`,
                  color: hero.subtitleColor || '#1e40af'
                }}
              >
                {hero.subtitle || 'Focus on high performance, high quality analog-digital ASIC'}
              </p>
              <div className="flex flex-wrap gap-4">
                <Button asChild size="lg" className="bg-blue-600 hover:bg-blue-700">
                  <Link to={hero.buttonLink || '/products'}>
                    {hero.buttonText || '浏览产品'} <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                </Button>
                <Button
                  asChild
                  size="lg"
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  <Link to="/about">了解更多 <ArrowRight className="ml-2 h-5 w-5" /></Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section className="py-20 bg-slate-100" style={productsIntro.backgroundImage ? { backgroundImage: `url(${productsIntro.backgroundImage})`, backgroundSize: 'cover', backgroundPosition: 'center' } : {}}>
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {productsIntro.title || '产品中心'}
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              {productsIntro.description || '瀚昕微可提供包括LDO线性稳压器、过压过流保护IC、线性充电IC、DCDC功率转换器、协议IC、LED驱动等系列产品。'}
            </p>
          </div>

          {/* Product Categories Slider - 图片+标题轮播 */}
          <div className="relative">
            <button
              onClick={() => scrollProducts('left')}
              className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-white shadow-lg rounded-full flex items-center justify-center hover:bg-gray-50 transition-colors"
            >
              <ChevronLeft className="h-6 w-6" />
            </button>
            <button
              onClick={() => scrollProducts('right')}
              className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-white shadow-lg rounded-full flex items-center justify-center hover:bg-gray-50 transition-colors"
            >
              <ChevronRight className="h-6 w-6" />
            </button>

            <div
              ref={productSliderRef}
              className="flex overflow-x-auto scrollbar-hide space-x-6 px-8 py-4"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {categories.map((category) => (
                <Link
                  key={category.id}
                  to={`/products?category=${category.id}`}
                  className="flex-shrink-0 w-64 group"
                >
                  <Card className="h-full hover:shadow-xl transition-shadow border-0 shadow-md overflow-hidden">
                    <div className="aspect-[4/3] relative overflow-hidden bg-gray-100">
                      <img
                        src={category.image || `https://images.unsplash.com/photo-${
                          category.id === '1' ? '1518770660439-4636190af475' :
                          category.id === '2' ? '1558618666-fcd25c85cd64' :
                          category.id === '3' ? '1565514020176-db9af97c5a5c' :
                          category.id === '4' ? '1451187580459-43490279c0fa' :
                          category.id === '5' ? '1557597774-9d273605dfa9' :
                          '1519389950476-29a8e752cc80'
                        }?w=400&q=80`}
                        alt={category.name}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                      <div className="absolute bottom-0 left-0 right-0 p-4">
                        <h3 className="text-white font-bold text-lg">{category.name}</h3>
                        <p className="text-white/80 text-sm">{category.nameEn}</p>
                      </div>
                    </div>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Applications Section - 轮播方式 */}
      <section className="py-20 bg-slate-100">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">应用领域</h2>
          </div>

          <div className="relative">
            <button
              onClick={() => scrollApps('left')}
              className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-white shadow-lg rounded-full flex items-center justify-center hover:bg-gray-50 transition-colors"
            >
              <ChevronLeft className="h-6 w-6" />
            </button>
            <button
              onClick={() => scrollApps('right')}
              className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-white shadow-lg rounded-full flex items-center justify-center hover:bg-gray-50 transition-colors"
            >
              <ChevronRight className="h-6 w-6" />
            </button>

            <div
              ref={appSliderRef}
              className="flex overflow-x-auto scrollbar-hide space-x-6 px-8 py-4"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {applications.map((app) => (
                <Link
                  key={app.id}
                  to={`/applications?id=${app.id}`}
                  className="flex-shrink-0 w-64 group"
                >
                  <Card className="h-full hover:shadow-xl transition-shadow border-0 shadow-md overflow-hidden">
                    <div className="aspect-[4/3] relative overflow-hidden">
                      <img
                        src={app.image || `https://images.unsplash.com/photo-${
                          app.id === '1' ? '1496181133206-80ce9b88a853' :
                          app.id === '2' ? '1581091226825-a6a2a5aee158' :
                          app.id === '3' ? '1504148451-1488729e17f8' :
                          app.id === '4' ? '1557597774-9d273605dfa9' :
                          '1451187580459-43490279c0fa'
                        }?w=400&q=80`}
                        alt={app.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                      <div className="absolute bottom-0 left-0 right-0 p-4">
                        <h3 className="text-white font-bold text-lg">{app.title}</h3>
                        <p className="text-white/80 text-sm">{app.titleEn}</p>
                      </div>
                    </div>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-20 bg-white text-gray-900" style={about.backgroundImage ? { backgroundImage: `url(${about.backgroundImage})`, backgroundSize: 'cover', backgroundPosition: 'center' } : {}}>
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold mb-6">{about.title || '关于瀚昕微'}</h2>
              <div 
                className="space-y-4 text-gray-600"
                style={{ 
                  fontSize: `${about.fontSize || 16}px`,
                  lineHeight: about.lineHeight || 1.8 
                }}
              >
                {(about.content || []).map((paragraph: string, index: number) => (
                  <p key={index}>{paragraph}</p>
                ))}
              </div>
              <Button asChild className="mt-8 bg-blue-600 hover:bg-blue-700">
                <Link to="/about">
                  了解更多 <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
            </div>
            <div className="relative">
              <div className="aspect-video rounded-xl overflow-hidden shadow-lg">
                <img
                  src={about.image || 'https://images.unsplash.com/photo-1565514020176-db9af97c5a5c?w=800&q=80'}
                  alt="Company"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Philosophy Section */}
      <section className="py-20 bg-slate-100" style={philosophy.backgroundImage ? { backgroundImage: `url(${philosophy.backgroundImage})`, backgroundSize: 'cover', backgroundPosition: 'center' } : {}}>
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {philosophy.title || '经营理念'}
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {(philosophy.items || []).map((item: any, index: number) => (
              <div 
                key={index} 
                className="relative overflow-hidden rounded-xl aspect-[3/4] group"
              >
                <img
                  src={item.image || 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=400&q=80'}
                  alt={item.title}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <h3 
                    className="text-2xl font-bold mb-2"
                    style={{ color: item.textColor || '#ffffff' }}
                  >
                    {item.title}
                  </h3>
                  <p 
                    style={{ 
                      color: item.textColor || '#ffffff',
                      fontSize: `${item.fontSize || 16}px`
                    }}
                  >
                    {item.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* News Section */}
      <section 
        className="py-20"
        style={pageContent.news?.backgroundImage ? {
          backgroundImage: `url(${pageContent.news.backgroundImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        } : { backgroundColor: '#ffffff' }}
      >
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900">新闻中心</h2>
            <Link to="/news" className="text-blue-400 hover:underline flex items-center">
              查看更多 <ArrowRight className="ml-1 h-4 w-4" />
            </Link>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {news.slice(0, 3).map((item, index) => (
              <Link key={item.id} to={`/news/${item.id}`}>
                <Card className="h-full hover:shadow-lg transition-shadow border-0 shadow-sm overflow-hidden bg-white/95">
                  <div className="aspect-video bg-gray-200 relative overflow-hidden">
                    <img
                      src={item.image || `https://images.unsplash.com/photo-${
                        index === 0
                          ? '1519389950476-29a8e752cc80'
                          : index === 1
                          ? '1558618666-fcd25c85cd64'
                          : '1460925895917-afdab827c52f'
                      }?w=400&q=80`}
                      alt={item.title}
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <CardContent className="p-6">
                    <div className="text-sm text-gray-500 mb-2">{item.date}</div>
                    <h3 className="font-bold text-gray-900 mb-2 line-clamp-2">{item.title}</h3>
                    <p className="text-gray-600 text-sm line-clamp-2">{item.summary}</p>
                    <div className="mt-4 flex items-center text-blue-600 text-sm font-medium">
                      了解详情 <ArrowRight className="ml-1 h-4 w-4" />
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Partners Section - 3*5阵列 */}
      <section 
        className="py-20"
        style={pageContent.partners?.backgroundImage ? {
          backgroundImage: `url(${pageContent.partners.backgroundImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        } : { backgroundColor: '#f1f5f9' }}
      >
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">合作伙伴</h2>
            <p className="text-gray-600">瀚昕微携手合作伙伴，共创美好未来！</p>
          </div>

          <div className="grid grid-cols-5 gap-6 max-w-5xl mx-auto">
            {(pageContent.partners?.partnerLogos || []).length > 0 ? (
              (pageContent.partners?.partnerLogos || []).slice(0, 15).map((partner, index) => (
                <div
                  key={index}
                  className="aspect-[3/2] flex items-center justify-center bg-white rounded-lg p-3 shadow-sm hover:shadow-md transition-shadow"
                >
                  {partner.image ? (
                    <img
                      src={partner.image}
                      alt={partner.name}
                      className="max-w-full max-h-full object-contain"
                    />
                  ) : (
                    <span className="text-sm font-bold text-gray-500 text-center">{partner.name}</span>
                  )}
                </div>
              ))
            ) : (
              ['TCL', '小米', '华为', 'OPPO', 'vivo', '联想', '比亚迪', '大疆', '美的', '格力', 
                '海尔', '海信', '长虹', '创维', '康佳'].map((partner, index) => (
                <div
                  key={index}
                  className="aspect-[3/2] flex items-center justify-center bg-white rounded-lg p-3 shadow-sm hover:shadow-md transition-shadow"
                >
                  <span className="text-lg font-bold text-gray-500">{partner}</span>
                </div>
              ))
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
